import { z } from 'zod';

/** Per-registration settings embedded in invite/registration tokens. */
export const tokenSettingsSchema = z.object({
    mustVerifyEmail: z.boolean().optional(),
    skipRegistrationTerms: z.boolean().optional(),
    showPopUpMessage: z.boolean().optional(),
});
export type TokenSettings = z.infer<typeof tokenSettingsSchema>;

/** The register-audience claims GET /auth/api/token returns verbatim after
 * verification — the picked claim set, never the raw jose payload. The
 * legacy client_id/redirect_uri claims (and `params`' legacy contents)
 * retire at P9 with the query adapter. */
export const tokenClaimsSchema = z.object({
    email: z.string().optional(),
    organization: z.string().optional(),
    orgName: z.string().optional(),
    inviterName: z.string().optional(),
    partnerId: z.string().optional(),
    role: z.string().optional(),
    /** Minted sign-in query (verification-email round trip). */
    params: z.string().optional(),
    isInvite: z.boolean().optional(),
    settings: tokenSettingsSchema.optional(),
    client_id: z.string().optional(),
    redirect_uri: z.string().optional(),
    redirectTo: z.string().optional(),
});
export type TokenClaims = z.infer<typeof tokenClaimsSchema>;

/** POST /auth/api/token body: the destination params the minted register
 * JWT embeds (round-tripped by the platform API into the verification
 * email's token). Opaque here; the consuming nav route re-vets it. */
export const tokenMintRequestSchema = z.object({
    params: z.string(),
});
export type TokenMintRequest = z.infer<typeof tokenMintRequestSchema>;

/** POST /auth/api/token 200. */
export const tokenMintResponseSchema = z.object({
    token: z.string(),
    tokenPayload: tokenClaimsSchema,
});
export type TokenMintResponse = z.infer<typeof tokenMintResponseSchema>;

/** GET /auth/api/session 200 (204 = no session cookie). isVerified mirrors
 * the session claim: only an explicit false is unverified. */
export const sessionVerdictSchema = z.object({
    email: z.string().nullable(),
    isVerified: z.boolean(),
});
export type SessionVerdict = z.infer<typeof sessionVerdictSchema>;

/** POST /auth/api/session body. The backend keeps its hand-rolled guard
 * (the 400 body is contract-pinned), so this types the sender side.
 * `scope: 'enterprise'` marks a login through the enterprise client pair —
 * the backend plants the token into the host-only session cookie when the
 * serving mount is the enterprise host (ENTERPRISE_HOST), and falls back to
 * the shared cookie everywhere else. A dishonest scope buys nothing: the
 * cookie only carries the token; role authz stays in each backend. */
export const sessionCreateRequestSchema = z.object({
    access_token: z.string(),
    scope: z.literal('enterprise').optional(),
});
export type SessionCreateRequest = z.infer<typeof sessionCreateRequestSchema>;

/** POST /auth/api/switch-account body (src/client.ts sends it; the
 * backend keeps its hand-rolled guard — that error body is pinned). */
export const switchAccountRequestSchema = z.object({
    account_id: z.string(),
});
export type SwitchAccountRequest = z.infer<typeof switchAccountRequestSchema>;

/** POST /auth/api/use-org-context body (same hand-rolled-guard convention as
 * switch-account). */
export const useOrgContextRequestSchema = z.object({
    account_id: z.string(),
});
export type UseOrgContextRequest = z.infer<typeof useOrgContextRequestSchema>;

/** GET /auth/api/user-exists 200. */
export const userExistsResponseSchema = z.object({
    exists: z.boolean(),
});
export type UserExistsResponse = z.infer<typeof userExistsResponseSchema>;
